import clsx from "clsx";
import React, { useEffect, useId, useRef, useState } from "react";
import type { AnimationItem } from "lottie-web";
import { config } from "~/config";

interface LottieStickerProps {
    lottieName: string;
    className?: string;
    fallback?: React.ReactElement;
}

const CDN_URL_PREFIX = `${config.cdn_host}/tg_json_stickers`;
const StickerReqPromiseMap = new Map<string, Promise<string>>();
const StickerCacheMap = new Map<string, string>();

const LottieSticker: React.FC<LottieStickerProps> = ({
    lottieName: tgsAssetName,
    className,
    fallback,
}) => {
    const id = useId();
    const lottieContainerRef = useRef<HTMLDivElement>(null);
    const lottieInstanceRef = useRef<AnimationItem>();
    const [_isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<Error | null>(null);

    const firstEffect = useRef(true);
    const firstCleanup = useRef(true);

    useEffect(() => {
        if (!firstEffect.current) return;
        firstEffect.current = false;
        setIsLoading(true);

        const fetchLottieJsonFile = () => {
            const cachedPromise = StickerReqPromiseMap.get(tgsAssetName);
            if (cachedPromise) {
                return cachedPromise;
            }

            const promise = fetch(`${CDN_URL_PREFIX}/${tgsAssetName}.json`)
                .then((response) => {
                    if (!response.ok) {
                        StickerReqPromiseMap.delete(tgsAssetName);
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    return response;
                })
                .then((response) => response.text())
                .then((data) => {
                    StickerCacheMap.set(tgsAssetName, data);
                    StickerReqPromiseMap.delete(tgsAssetName);
                    return data;
                });
            StickerReqPromiseMap.set(tgsAssetName, promise);
            return promise;
        };

        const readAnimDataFromCache = async () => {
            const animData = StickerCacheMap.get(tgsAssetName);
            return animData ? animData : fetchLottieJsonFile();
        };

        readAnimDataFromCache()
            .then((animData) => {
                return Promise.all([
                    import("lottie-web").then((module) => module.default),
                    animData,
                ]);
            })
            .then(([lottie, animData]) => {
                try {
                    const animationConfig = {
                        name: id,
                        container: lottieContainerRef.current as HTMLDivElement,
                        animationData: JSON.parse(animData),
                        autoplay: true,
                        loop: true,
                    };

                    lottieInstanceRef.current =
                        lottie.loadAnimation(animationConfig);
                } catch (e) {
                    throw new Error(`Failed to render TGS: ${tgsAssetName}`);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoadError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });

        const visibilitychange = () => {
            const lottieItem = lottieInstanceRef.current;
            if (!lottieItem) return;

            if (document.visibilityState === "visible") {
                lottieItem.play();
            } else {
                lottieItem.pause();
            }
        };

        const onPageBlur = () => {
            const lottieItem = lottieInstanceRef.current;
            if (!lottieItem) return;
            lottieItem.pause();
        };

        const onPageFocus = () => {
            const lottieItem = lottieInstanceRef.current;
            if (!lottieItem) return;
            lottieItem.play();
        };

        document.addEventListener("visibilitychange", visibilitychange);
        window.addEventListener("blur", onPageBlur);
        window.addEventListener("focus", onPageFocus);

        return () => {
            if (!firstCleanup.current) return;
            firstCleanup.current = false;
            lottieInstanceRef.current?.destroy(id);
            window.removeEventListener("blur", onPageBlur);
            window.removeEventListener("focus", onPageFocus);
            document.removeEventListener("visibilitychange", visibilitychange);
        };
    }, []);

    return (
        <>
            <div
                className={clsx({ hidden: loadError }, className)}
                ref={lottieContainerRef}
            />
            {loadError && fallback}
        </>
    );
};

export default LottieSticker;
